import { useCallback, useEffect, useState } from "react";
import {
	BccRecipientList,
	useGetAllContractorBccRecipientListsLazyQuery,
	useUpdateBccRecipientListMutation,
} from "@repo/server-api";
import { useNotification } from "@repo/shared/components";
import { useTranslation } from "react-i18next";

import { BccRecipientListModel, SUPPORTED_EMAIL_TYPES } from "../types/BccRecipientListTypes";

/**
 * Maps backend BccRecipientList to frontend model
 */
function mapBeToFe(beModel: BccRecipientList): BccRecipientListModel {
	return {
		contractorId: beModel.contractorId,
		type: beModel.type,
		recipients: [...beModel.recipients],
	};
}

/**
 * Creates frontend models for all supported email types,
 * merging with existing backend data
 */
function createModelsForAllTypes(
	contractorId: string,
	existingLists: BccRecipientList[],
): BccRecipientListModel[] {
	return SUPPORTED_EMAIL_TYPES.map((type) => {
		const existing = existingLists.find((list) => list.type === type);
		if (existing) {
			return mapBeToFe(existing);
		}
		return {
			contractorId,
			type,
			recipients: [],
		};
	});
}

export type UseBccRecipientListsResult = {
	/** List of all BCC recipient lists (one per email type) */
	lists: BccRecipientListModel[];
	/** Loading state */
	isLoading: boolean;
	/** Saving state */
	isSaving: boolean;
	/** Reload data from backend */
	reload: () => void;
	/** Save a BCC recipient list */
	save: (model: BccRecipientListModel) => Promise<boolean>;
};

/**
 * Hook for managing BCC recipient lists for a contractor
 * Handles loading, saving, and BE↔FE model mapping
 */
export function useBccRecipientLists(contractorId: string | null): UseBccRecipientListsResult {
	const { t } = useTranslation();
	const { showNotification } = useNotification();

	const [lists, setLists] = useState<BccRecipientListModel[]>([]);
	const [isSaving, setIsSaving] = useState(false);

	// Load query
	const [loadLists, { loading: isLoading }] = useGetAllContractorBccRecipientListsLazyQuery({
		fetchPolicy: "no-cache",
		onCompleted(data) {
			if (data?.allContractorBccRecipientLists && contractorId) {
				const models = createModelsForAllTypes(contractorId, data.allContractorBccRecipientLists);
				setLists(models);
			}
		},
		onError(error) {
			showNotification(error.message, "error");
		},
	});

	// Save mutation
	const [updateMutation] = useUpdateBccRecipientListMutation();

	// Load data when contractorId changes
	useEffect(() => {
		if (contractorId) {
			loadLists({ variables: { contractorId } });
		} else {
			setLists([]);
		}
	}, [contractorId, loadLists]);

	// Reload function
	const reload = useCallback(() => {
		if (contractorId) {
			loadLists({ variables: { contractorId } });
		}
	}, [contractorId, loadLists]);

	// Save function with FE→BE mapping
	const save = useCallback(
		async (model: BccRecipientListModel): Promise<boolean> => {
			setIsSaving(true);
			try {
				const result = await updateMutation({
					variables: {
						contractorId: model.contractorId,
						type: model.type,
						recipients: model.recipients,
					},
				});

				if (result.data?.updateBccRecipientList.boolean) {
					showNotification(t("bcc-recipient-list.updated"), "success");
					reload();
					return true;
				}

				if (result.data?.updateBccRecipientList.errors) {
					result.data.updateBccRecipientList.errors.forEach((error) => {
						if ("message" in error) {
							showNotification(error.message, "error");
						}
					});
				}

				return false;
			} catch (error) {
				showNotification(
					error instanceof Error ? error.message : t("bcc-recipient-list.unknown-error"),
					"error",
				);
				return false;
			} finally {
				setIsSaving(false);
			}
		},
		[updateMutation, reload, showNotification, t],
	);

	return {
		lists,
		isLoading,
		isSaving,
		reload,
		save,
	};
}
